// Arquivo deixado propositalmente vazio. O client ativo está em
// Módulo de infra centralizado.
// HTTP Client (frontend) - infraestrutura central em chassis
// Uso: importar de 'src/chassis/http/client'

import { emitSessionExpired } from '@/chassis/core/auth/session-events';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiErrorPayload {
    code?: string | number;
    message: string;
    details?: any;
    status?: number;
}

export class ApiError extends Error {
    status?: number;
    code?: string | number;
    details?: any;
    constructor(payload: ApiErrorPayload) {
        super(payload.message || 'API error');
        this.name = 'ApiError';
        this.status = payload.status;
        this.code = payload.code;
        this.details = payload.details;
    }
}

export interface RequestOptions<TBody = any> {
    method?: HttpMethod;
    headers?: Record<string, string>;
    query?: Record<string, any>;
    body?: TBody;
    rawBody?: BodyInit;
    signal?: AbortSignal;
    allowLegacyPath?: boolean;
}

function buildQuery(params?: Record<string, any>): string {
    if (!params) return '';
    const entries: [string, string][] = [];
    for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null) continue;
        if (Array.isArray(v)) {
            v.forEach((item) => entries.push([k, String(item)]));
        } else if (typeof v === 'object') {
            for (const [sk, sv] of Object.entries(v)) {
                if (sv === undefined || sv === null) continue;
                entries.push([`${k}.${sk}`, String(sv)]);
            }
        } else {
            entries.push([k, String(v)]);
        }
    }
    const qs = new URLSearchParams(entries).toString();
    return qs ? `?${qs}` : '';
}

const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

const legacyDomainPrefixPattern = /^\/(syd|opd|agd|odd|igd|add)-/i;
const AUTH_ROUTES_IGNORING_SESSION_EXPIRY = new Set([
    '/authentication/login',
    '/authentication/logout',
    '/authentication/refresh',
    '/authentication/rate-limit-info',
]);
// Bug N20 (2026-04-28): 403 removido daqui. Forbidden NAO eh sessao
// expirada — significa "user esta autenticado, mas nao tem permissao para
// ESTA acao especifica". Redirecionar pra login nesse caso desloga o user
// indevidamente (reproduzido no botao "Gerar Ata agora" pra licitacao
// deserta — BE retornou 403, FE empurrou pro /login). Caller deve receber
// o ApiError(403) e exibir toast de erro. 401 (token invalido) e 419/440
// (timeouts de sessao) continuam disparando logout — esses sim sao sessao
// expirada legitima.
const SESSION_EXPIRY_HTTP_STATUSES = new Set([401, 419, 440]);
const REFRESH_ENDPOINT = '/authentication/refresh';
let refreshPromise: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('auth.refreshToken');
    if (!refreshToken) return false;

    try {
        const resp = await fetch(`${baseUrl}${REFRESH_ENDPOINT}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
        });
        if (!resp.ok) return false;

        const data = await resp.json();
        if (data?.success && data?.data?.accessToken) {
            localStorage.setItem('auth.token', data.data.accessToken);
            if (data.data.refreshToken) localStorage.setItem('auth.refreshToken', data.data.refreshToken);
            return true;
        }
        return false;
    } catch {
        return false;
    }
}

function ensureLeadingSlash(path: string): string {
    return path.startsWith('/') ? path : `/${path}`;
}

function normalizeApiPath(path: string): string {
    if (!path) return path;
    const normalized = ensureLeadingSlash(path);
    if (legacyDomainPrefixPattern.test(normalized)) {
        throw new Error(`Legacy domain prefix detected in API path: "${normalized}". Update the caller to use the controller route without the domain tag.`);
    }
    return normalized;
}

function defaultHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
        Accept: 'application/json',
    };
    const token = localStorage.getItem('auth.token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    headers['X-Request-ID'] = globalThis.crypto && 'randomUUID' in globalThis.crypto ? (globalThis.crypto as any).randomUUID() : `${Date.now()}-${Math.random()}`;
    return headers;
}

function isApiKeyError(code: ApiErrorPayload['code'], message: ApiErrorPayload['message']): boolean {
    const normalizedMessage = typeof message === 'string' ? message.toLowerCase() : '';
    return (
        code === 'API_KEY_MISSING' ||
        code === 'API_KEY_INVALID' ||
        normalizedMessage.includes('api key') ||
        normalizedMessage.includes('x-api-key') ||
        normalizedMessage.includes('apikey')
    );
}

// Bug 16/05 Alexandre: fornecedor reportou que ao clicar 'Enviar Lance' o
// sistema desloga. Causa: token JWT curto + endpoint /bids/place com latencia
// alta (varias validacoes — intervalo, incremento, fase, credenciamento). Se
// o token expira no meio do roundtrip, BE retorna 401, refresh+retry no
// http-client tambem pode falhar (Redis session pode estar limpa pelo race),
// e o erro final dispara sessionExpired -> logout no meio da disputa, que
// e catastrofico (forn perde a janela de lances). Mantemos retry com refresh
// igual antes, mas pra rotas criticas em disputa NAO emitimos sessionExpired
// em 401 final — o caller (sub-tab-dispute-room) ja tem catch que mostra
// 'Falha ao enviar lance' e o user pode tentar de novo sem perder a sessao.
const CRITICAL_DISPUTE_ROUTES_IGNORING_SESSION_EXPIRY = [
    '/bids/place',
    '/bids/',  // withdraw, updates
];

function isCriticalDisputeRoute(path: string): boolean {
    return CRITICAL_DISPUTE_ROUTES_IGNORING_SESSION_EXPIRY.some((prefix) => path.startsWith(prefix));
}

function shouldEmitSessionExpiredEvent(path: string, status: number, code: ApiErrorPayload['code'], message: ApiErrorPayload['message']): boolean {
    if (AUTH_ROUTES_IGNORING_SESSION_EXPIRY.has(path)) return false;
    if (!SESSION_EXPIRY_HTTP_STATUSES.has(status)) return false;
    if (path.startsWith('/external/v1/')) return false;
    if (isApiKeyError(code, message)) return false;
    if (isCriticalDisputeRoute(path)) return false;
    return true;
}

async function parseJsonSafely(resp: Response): Promise<any> {
    const text = await resp.text();
    if (!text) return undefined;
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

export async function httpRequest<T = any, TBody = any>(path: string, opts: RequestOptions<TBody> = {}): Promise<T> {
    const normalizedPath = opts.allowLegacyPath ? ensureLeadingSlash(path) : normalizeApiPath(path);
    const url = `${baseUrl}${normalizedPath}${buildQuery(opts.query)}`;
    const headers: Record<string, string> = { ...defaultHeaders(), ...(opts.headers || {}) };
    let body: BodyInit | undefined = undefined;
    if (opts.rawBody !== undefined) {
        body = opts.rawBody;
    } else if (opts.body !== undefined) {
        headers['Content-Type'] = headers['Content-Type'] || 'application/json';
        body = headers['Content-Type'] === 'application/json' ? JSON.stringify(opts.body) : (opts.body as any);
    }

    const resp = await fetch(url, {
        method: opts.method || 'GET',
        headers,
        body,
        signal: opts.signal,
        credentials: 'include',
    });

    if (!resp.ok) {
        // Tentar refresh token automaticamente em caso de 401
        if (resp.status === 401 && !normalizedPath.startsWith('/authentication/')) {
            // Evitar múltiplos refreshes simultâneos
            if (!refreshPromise) {
                refreshPromise = tryRefreshToken().finally(() => { refreshPromise = null; });
            }
            const refreshed = await refreshPromise;
            if (refreshed) {
                // Retry com novo token
                const retryHeaders = { ...defaultHeaders(), ...(opts.headers || {}) };
                if (opts.body !== undefined && !opts.rawBody) {
                    retryHeaders['Content-Type'] = retryHeaders['Content-Type'] || 'application/json';
                }
                const retryResp = await fetch(url, {
                    method: opts.method || 'GET',
                    headers: retryHeaders,
                    body,
                    signal: opts.signal,
                    credentials: 'include',
                });
                if (retryResp.ok) {
                    return await parseJsonSafely(retryResp) as T;
                }
            }
        }

        const data = await parseJsonSafely(resp);
        const normalized: ApiErrorPayload = {
            status: resp.status,
            code: (data && (data.code || data.errorCode)) || resp.status,
            message: (data && (data.message || data.error || data.title)) || `HTTP ${resp.status}`,
            details: data && (data.details || data.errors || data),
        };

        if (shouldEmitSessionExpiredEvent(normalizedPath, resp.status, normalized.code, normalized.message)) {
            emitSessionExpired({
                reason: typeof normalized.message === 'string' ? normalized.message : undefined,
                status: resp.status,
                path: normalizedPath,
            });
        }
        throw new ApiError(normalized);
    }

    const data = await parseJsonSafely(resp);
    return data as T;
}

export const http = {
    get: <T = any>(path: string, query?: Record<string, any>, options?: Omit<RequestOptions, 'method' | 'query'>) => httpRequest<T>(path, { ...options, method: 'GET', query }),
    post: <T = any, TBody = any>(path: string, body?: TBody, options?: Omit<RequestOptions<TBody>, 'method' | 'body'>) => httpRequest<T, TBody>(path, { ...options, method: 'POST', body }),
    put: <T = any, TBody = any>(path: string, body?: TBody, options?: Omit<RequestOptions<TBody>, 'method' | 'body'>) => httpRequest<T, TBody>(path, { ...options, method: 'PUT', body }),
    patch: <T = any, TBody = any>(path: string, body?: TBody, options?: Omit<RequestOptions<TBody>, 'method' | 'body'>) => httpRequest<T, TBody>(path, { ...options, method: 'PATCH', body }),
    delete: <T = any>(path: string, options?: Omit<RequestOptions, 'method'>) => httpRequest<T>(path, { ...options, method: 'DELETE' }),
};

export function resolveApiUrl(path: string, options: { allowLegacyPath?: boolean } = {}): string {
    const normalizedPath = options.allowLegacyPath ? ensureLeadingSlash(path) : normalizeApiPath(path);
    return `${baseUrl}${normalizedPath}`;
}

export type PaginatedResult<TItem = any> = {
    items: TItem[];
    pagination?: {
        totalItems?: number;
        totalPages?: number;
        currentPage?: number;
        itemsPerPage?: number;
        hasNext?: boolean;
        hasPrev?: boolean;
    };
    summary?: any;
    appliedFilters?: any;
};
